/**
 * Admin — Admissions extras: Merit list · Allocation planner · Settings.
 * All three ride on migration 034_admissions_extras.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, InputNumber, Modal, Popconfirm, Select, Skeleton,
  Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  AdmissionFormField, AdmissionQuota, AdmissionRequiredDoc, AdmissionSettingsPayload,
  AllocationCandidate, AllocationSection, FormFieldKind, MeritRow, PlanAllocationRequest,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

const QUOTAS: AdmissionQuota[] = ['open', 'reserved', 'staff', 'management', 'other'];
const QUOTA_TONE: Record<AdmissionQuota, string> = {
  open: 'blue', reserved: 'purple', staff: 'geekblue', management: 'gold', other: 'default',
};

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/* =========================== Merit =========================== */

export function MeritPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [grade, setGrade] = useState<string>('');
  const [editing, setEditing] = useState<MeritRow | null>(null);

  const q = useQuery({
    queryKey: ['merit', institution, grade],
    queryFn: () => productApi.getMerit(institution, grade || undefined),
    enabled: !!institution,
  });
  const finalize = useMutation({
    mutationFn: () => productApi.finalizeMerit(institution, { grade_applied: grade }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['merit', institution] });
      void message.success(`Finalized · ${r.ranked} applications ranked`);
    },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Finalize failed'),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  const csvHref = grade
    ? `data:text/csv;charset=utf-8,${encodeURIComponent(toMeritCsv(q.data.rows))}`
    : '';

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Admissions merit</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Rank by merit_score, break ties on sibling flag, then created_at.
            {q.data.finalized_at && ` · Finalized ${new Date(q.data.finalized_at).toLocaleString()}.`}
          </Typography.Text>
        </div>
        <Input size="small" placeholder="Filter by grade applied" value={grade}
          onChange={(e) => setGrade(e.target.value)} style={{ width: 220 }} />
        <Button icon={<DownloadOutlined />} disabled={!grade || q.data.rows.length === 0}
          href={csvHref}
          download={`merit-${grade || 'all'}.csv`}
        >CSV</Button>
        <Popconfirm title={`Finalize merit for grade "${grade}"?`} onConfirm={() => finalize.mutate()}
          disabled={!grade || q.data.rows.length === 0}>
          <Button type="primary" loading={finalize.isPending} disabled={!grade}>Finalize</Button>
        </Popconfirm>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
        {QUOTAS.map((k) => (
          <Card key={k} size="small">
            <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{q.data.by_quota[k] ?? 0}</div>
          </Card>
        ))}
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description={grade ? `No applications for "${grade}".` : 'No applications yet.'} style={{ padding: 20 }} />
          : (
            <Table<MeritRow>
              size="small" pagination={{ pageSize: 25 }} rowKey="application_id"
              dataSource={q.data.rows}
              onRow={(r) => ({ onClick: () => setEditing(r), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Rank', dataIndex: 'merit_rank', key: 'r', width: 70,
                  render: (v: number | null) => v ? <b>#{v}</b> : <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Code', dataIndex: 'code', key: 'c', width: 140,
                  render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                { title: 'Child', dataIndex: 'child_name', key: 'n' },
                { title: 'Grade', dataIndex: 'grade_applied', key: 'g', width: 80 },
                { title: 'Quota', dataIndex: 'quota', key: 'q', width: 110,
                  render: (v: AdmissionQuota) => <Tag color={QUOTA_TONE[v]}>{v}</Tag> },
                { title: 'Sibling', dataIndex: 'sibling', key: 's', width: 80,
                  render: (v: boolean) => v ? <Tag color="success">yes</Tag> : <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Score', dataIndex: 'merit_score', key: 'sc', width: 90, align: 'right',
                  render: (v: number | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Status', dataIndex: 'status', key: 'st', width: 100,
                  render: (v: string) => <Tag>{v}</Tag> },
              ]}
            />
          )}
      </Card>

      <ScoreModal target={editing} institution={institution}
        onClose={() => setEditing(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ['merit', institution] })} />
    </Space>
  );
}

function toMeritCsv(rows: MeritRow[]): string {
  const header = ['rank', 'code', 'child_name', 'grade', 'quota', 'sibling', 'score', 'status'].join(',');
  const body = rows.map((r) => [
    r.merit_rank ?? '', r.code, esc(r.child_name), r.grade_applied,
    r.quota, r.sibling ? '1' : '0', r.merit_score ?? '', r.status,
  ].join(','));
  return [header, ...body].join('\n');
}
function esc(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function ScoreModal({
  target, institution, onClose, onDone,
}: { target: MeritRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const [score, setScore] = useState<number | null>(null);
  const [sibling, setSibling] = useState(false);
  const [quota, setQuota] = useState<AdmissionQuota>('open');
  const [seeded, setSeeded] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => productApi.scoreApplication(institution, target!.code, {
      merit_score: score, sibling, quota,
    }),
    onSuccess: () => { void message.success('Saved'); onDone(); onClose(); },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Save failed'),
  });
  // Seed on open
  if (target && seeded !== target.application_id) {
    setSeeded(target.application_id);
    setScore(target.merit_score);
    setSibling(target.sibling);
    setQuota(target.quota);
  }
  return (
    <Modal open={!!target} title={target ? `Score · ${target.child_name}` : ''}
      onCancel={() => { m.reset(); onClose(); }} okText="Save"
      confirmLoading={m.isPending} onOk={() => m.mutate()} destroyOnClose>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Merit score (0–1000)</div>
          <InputNumber value={score ?? undefined} onChange={(v) => setScore(typeof v === 'number' ? v : null)}
            min={0} max={1000} step={0.5} style={{ width: '100%' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Sibling on record</div>
          <Switch checked={sibling} onChange={setSibling} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Quota</div>
          <Select value={quota} onChange={(v) => setQuota(v as AdmissionQuota)}
            style={{ width: '100%' }}
            options={QUOTAS.map((k) => ({ value: k, label: k }))} />
        </div>
      </Space>
    </Modal>
  );
}

/* =========================== Allocation =========================== */

export function AllocationPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [grade, setGrade] = useState('');
  const [sections, setSections] = useState<PlanAllocationRequest['section_capacities']>(
    [{ section: 'A', capacity: 40 }],
  );
  const [plan, setPlan] = useState<import('@velora/contracts').AllocationPlanResponse | null>(null);
  const run = useMutation({
    mutationFn: (input: PlanAllocationRequest) => productApi.planAllocation(institution, input),
    onSuccess: (r) => setPlan(r),
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Plan failed'),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Section allocation</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Balances gender + merit rank in one pass. This is a proposal — no writes.
        </Typography.Text>
      </div>

      <Card size="small">
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Space size={8}>
            <Input placeholder="Grade applied (e.g. Grade 1)" value={grade}
              onChange={(e) => setGrade(e.target.value)} style={{ width: 240 }} />
            <Button type="primary" disabled={!grade || sections.length === 0}
              loading={run.isPending}
              onClick={() => run.mutate({ grade_applied: grade, section_capacities: sections })}>
              Plan
            </Button>
          </Space>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            {sections.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
                <Input size="small" placeholder="Section" value={s.section}
                  onChange={(e) => setSections((prev) => prev.map((p, ix) => ix === i ? { ...p, section: e.target.value } : p))}
                  style={{ width: 90 }} />
                <InputNumber size="small" min={0} max={300} value={s.capacity}
                  onChange={(v) => setSections((prev) => prev.map((p, ix) => ix === i ? { ...p, capacity: Number(v ?? 0) } : p))}
                  style={{ width: 100 }} />
                <Button size="small" icon={<DeleteOutlined />} danger
                  onClick={() => setSections((prev) => prev.filter((_, ix) => ix !== i))} />
              </div>
            ))}
            <Button size="small" icon={<PlusOutlined />}
              onClick={() => setSections((p) => [...p, { section: String.fromCharCode(65 + p.length), capacity: 40 }])}>
              Add section
            </Button>
          </div>
        </Space>
      </Card>

      {plan && <PlanView plan={plan} />}
    </Space>
  );
}

function PlanView({ plan }: { plan: import('@velora/contracts').AllocationPlanResponse }) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title={`Sections · unassigned ${plan.unassigned}`}>
        <Table<AllocationSection>
          size="small" pagination={false} rowKey="section" dataSource={plan.sections}
          columns={[
            { title: 'Section', dataIndex: 'section', key: 's' },
            { title: 'Capacity', dataIndex: 'capacity', key: 'c', align: 'right', width: 100 },
            { title: 'Existing', key: 'e', align: 'right', width: 130,
              render: (_, r) => `${r.current_boys}b / ${r.current_girls}g` },
            { title: 'Proposed', key: 'p', align: 'right', width: 140,
              render: (_, r) => <><b>{r.proposed_seats}</b> · {r.proposed_boys}b / {r.proposed_girls}g</> },
            { title: 'Free', key: 'f', align: 'right', width: 90,
              render: (_, r) => r.capacity - (r.current_boys + r.current_girls + r.proposed_seats) },
          ]}
        />
      </Card>
      <Card size="small" title={`Candidates (${plan.candidates.length})`}>
        <Table<AllocationCandidate>
          size="small" pagination={{ pageSize: 25 }} rowKey="application_id"
          dataSource={plan.candidates}
          columns={[
            { title: 'Rank', dataIndex: 'merit_rank', key: 'r', width: 70,
              render: (v: number | null) => v ? <b>#{v}</b> : <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Code', dataIndex: 'code', key: 'c', width: 140,
              render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
            { title: 'Child', dataIndex: 'child_name', key: 'n' },
            { title: 'Gender', dataIndex: 'gender', key: 'g', width: 80 },
            { title: 'Quota', dataIndex: 'quota', key: 'q', width: 100,
              render: (v: AdmissionQuota) => <Tag color={QUOTA_TONE[v]}>{v}</Tag> },
            { title: 'Sibling', dataIndex: 'sibling', key: 'sib', width: 90,
              render: (v: boolean) => v ? <Tag color="success">yes</Tag> : <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Section', dataIndex: 'suggested_section', key: 'ss', width: 100,
              render: (v: string | null) => v ? <Tag color="blue">{v}</Tag> : <Tag color="error">unassigned</Tag> },
            { title: 'Reason', dataIndex: 'reason', key: 'why',
              render: (v: string) => <span style={{ fontSize: 12, color: '#5b6478' }}>{v}</span> },
          ]}
        />
      </Card>
    </Space>
  );
}

/* =========================== Settings =========================== */

const FIELD_KINDS: FormFieldKind[] = ['text', 'phone', 'email', 'date', 'select', 'longtext', 'number'];

export function AdmissionSettingsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admission-settings', institution],
    queryFn: () => productApi.getAdmissionSettings(institution),
    enabled: !!institution,
  });
  const [form, setForm] = useState<AdmissionFormField[]>([]);
  const [docs, setDocs] = useState<AdmissionRequiredDoc[]>([]);
  const [seeded, setSeeded] = useState(false);
  if (q.data && !seeded) {
    setSeeded(true);
    setForm(q.data.form);
    setDocs(q.data.required_docs);
  }
  const save = useMutation({
    mutationFn: (input: AdmissionSettingsPayload) => productApi.updateAdmissionSettings(institution, input),
    onSuccess: () => {
      void message.success('Saved');
      void qc.invalidateQueries({ queryKey: ['admission-settings', institution] });
    },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Save failed'),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Admission settings</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Public form fields and required documents. Embedded application form at
            {' '}<code>/apply/&#123;institution&#125;</code> reads this config.
          </Typography.Text>
        </div>
        <Button type="primary" loading={save.isPending}
          onClick={() => save.mutate({ form, required_docs: docs })}>Save</Button>
      </div>

      <Card size="small" title={`Form fields (${form.length})`}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {form.map((f, i) => (
            <FieldRow key={i} f={f}
              onChange={(nf) => setForm((prev) => prev.map((p, ix) => ix === i ? nf : p))}
              onRemove={() => setForm((prev) => prev.filter((_, ix) => ix !== i))} />
          ))}
          <Button size="small" icon={<PlusOutlined />}
            onClick={() => setForm((prev) => [...prev, { key: `field_${prev.length + 1}`, label: 'New field', kind: 'text', required: false }])}>
            Add field
          </Button>
        </Space>
      </Card>

      <Card size="small" title={`Required documents (${docs.length})`}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {docs.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <Input size="small" value={d.key} placeholder="key"
                style={{ width: 140, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                onChange={(e) => setDocs((prev) => prev.map((p, ix) => ix === i ? { ...p, key: e.target.value } : p))} />
              <Input size="small" value={d.label} placeholder="label"
                onChange={(e) => setDocs((prev) => prev.map((p, ix) => ix === i ? { ...p, label: e.target.value } : p))} />
              <Switch size="small" checked={d.required}
                onChange={(v) => setDocs((prev) => prev.map((p, ix) => ix === i ? { ...p, required: v } : p))} />
              <span style={{ fontSize: 11, color: '#8a94a6' }}>required</span>
              <Button size="small" icon={<DeleteOutlined />} danger
                onClick={() => setDocs((prev) => prev.filter((_, ix) => ix !== i))} />
            </div>
          ))}
          <Button size="small" icon={<PlusOutlined />}
            onClick={() => setDocs((prev) => [...prev, { key: `doc_${prev.length + 1}`, label: 'New document', required: false }])}>
            Add document
          </Button>
        </Space>
      </Card>

      <Alert
        type="info" showIcon
        message="Landing / embed"
        description={<>The public embed at <Link to="#">/apply/{'{institution}'}</Link> reads this config. Landing page ships with the Landingpage iteration.</>}
      />
    </Space>
  );
}

function FieldRow({
  f, onChange, onRemove,
}: { f: AdmissionFormField; onChange: (f: AdmissionFormField) => void; onRemove: () => void }) {
  const isSelect = f.kind === 'select';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 130px 90px 40px', gap: 8, alignItems: 'baseline' }}>
      <Input size="small" value={f.key} placeholder="key"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        onChange={(e) => onChange({ ...f, key: e.target.value })} />
      <Input size="small" value={f.label} placeholder="label"
        onChange={(e) => onChange({ ...f, label: e.target.value })} />
      <Select size="small" value={f.kind} onChange={(v) => onChange({ ...f, kind: v as FormFieldKind })}
        options={FIELD_KINDS.map((k) => ({ value: k, label: k }))} />
      <div>
        <Switch size="small" checked={f.required} onChange={(v) => onChange({ ...f, required: v })} />
        <span style={{ fontSize: 11, color: '#8a94a6', marginLeft: 4 }}>required</span>
      </div>
      <Button size="small" icon={<DeleteOutlined />} danger onClick={onRemove} />
      {isSelect && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Input size="small" placeholder="options, comma-separated"
            value={(f.options ?? []).join(', ')}
            onChange={(e) => onChange({ ...f, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
        </div>
      )}
    </div>
  );
}
